import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import {
  clearSupportImpersonationSession,
  getSupportImpersonationSession,
  isSupportImpersonationErrorCode,
  setSupportImpersonationSession,
  type SupportImpersonationSession,
} from '@/features/support/utils/support-impersonation-session.util';

export interface SupportCandidate {
  userId: string;
  email: string;
  name: string | null;
  companyName: string | null;
}

interface SupportImpersonationState {
  activeSession: SupportImpersonationSession | null;
  searchResults: SupportCandidate[];
  isSearching: boolean;
  isStarting: boolean;
  isStopping: boolean;
  error: string | null;
  syncFromStorage: () => void;
  searchCandidates: (query: string) => Promise<void>;
  startImpersonation: (candidate: SupportCandidate, reason?: string) => Promise<boolean>;
  stopImpersonation: () => Promise<boolean>;
  clearSearchResults: () => void;
}

export const useSupportImpersonationStore = create<SupportImpersonationState>((set, get) => ({
  activeSession: getSupportImpersonationSession(),
  searchResults: [],
  isSearching: false,
  isStarting: false,
  isStopping: false,
  error: null,

  syncFromStorage: () => {
    set({ activeSession: getSupportImpersonationSession() });
  },

  searchCandidates: async (query) => {
    try {
      set({ isSearching: true, error: null });
      const response = await api.get('/api/support/impersonation/candidates', {
        params: { query: query.trim() },
      });

      set({
        searchResults: Array.isArray(response.data?.users) ? response.data.users : [],
        isSearching: false,
      });
    } catch (error: any) {
      logger.error('SupportImpersonationStore', 'Failed to search impersonation candidates', {
        error,
      });
      set({
        error: error?.response?.data?.error || 'Failed to search broker accounts.',
        isSearching: false,
      });
      toast.error('Failed to search broker accounts.');
    }
  },

  startImpersonation: async (candidate, reason = '') => {
    try {
      set({ isStarting: true, error: null });
      const response = await api.post('/api/support/impersonation/session', {
        targetUserId: candidate.userId,
        reason: reason.trim() || null,
      });

      const session = response.data?.session as SupportImpersonationSession | undefined;
      const token = response.data?.token as string | undefined;

      if (!session || !token) {
        throw new Error('Invalid impersonation session response.');
      }

      const activeSession: SupportImpersonationSession = {
        ...session,
        token,
      };

      setSupportImpersonationSession(activeSession);
      set({ activeSession, isStarting: false });
      toast.success(`Now impersonating ${candidate.email}`);
      return true;
    } catch (error: any) {
      logger.error('SupportImpersonationStore', 'Failed to start support impersonation', {
        error,
        candidate,
      });
      set({
        error: error?.response?.data?.error || 'Failed to start support impersonation.',
        isStarting: false,
      });
      toast.error(error?.response?.data?.error || 'Failed to start support impersonation.');
      return false;
    }
  },

  stopImpersonation: async () => {
    const activeSession = get().activeSession || getSupportImpersonationSession();

    if (!activeSession) {
      clearSupportImpersonationSession();
      set({ activeSession: null });
      return true;
    }

    try {
      set({ isStopping: true, error: null });
      await api.delete(`/api/support/impersonation/session/${activeSession.sessionId}`);
    } catch (error: any) {
      logger.warn('SupportImpersonationStore', 'Failed to end support impersonation session cleanly', {
        error,
        activeSession,
      });

      const errorCode = error?.response?.data?.errorCode;
      if (!isSupportImpersonationErrorCode(errorCode)) {
        toast.error(error?.response?.data?.error || 'Failed to end support impersonation cleanly.');
        set({
          error: error?.response?.data?.error || 'Failed to end support impersonation cleanly.',
        });
        set({ isStopping: false });
        return false;
      }
    }

    clearSupportImpersonationSession();
    set({ activeSession: null, isStopping: false, error: null });
    toast.success('Returned to your support account');
    return true;
  },

  clearSearchResults: () => {
    set({ searchResults: [], error: null });
  },
}));
